/**
 * SQL string/comment masker — the single text pass every "does this SQL do X?"
 * scanner runs before its regex (`ddlDetector.isSchemaDdl`,
 * `databaseDdlDetector.isDatabaseDdl`,
 * `navigational-features/index/ddlClassifier`, `intellisense` slot detectors).
 *
 * Extracted from `ddlDetector.ts` when the per-engine delimiter table outgrew
 * that module; `ddlDetector.ts` re-exports everything here, so existing imports
 * keep working.
 *
 * CROSS-ENGINE PARITY — the RULE below is identical on every engine: mask
 * string literals and comments with equal-length runs of spaces, copy
 * delimited identifiers through as opaque spans. Only the TABLE differs, and
 * it lives in `sqlDelimiterPolicy.ts` — with the per-engine WHY beside each
 * row; where a span the table opened ENDS is `sqlStringSpans.ts`, and the
 * MySQL `sql_mode` derivations that flip two of the table's cells are
 * `mysqlSqlMode.ts`. This module re-exports those modules' public names, so a
 * caller needs one import.
 */

import { type MaskOptions, policyFor } from './sqlDelimiterPolicy';
import {
  delimitedSpanEnd,
  dollarQuotedSpanEnd,
  isEscapeStringLead,
  isIdentifierChar,
  Q_QUOTE_LEAD,
  qQuotedSpanEnd,
} from './sqlStringSpans';

export { type MaskOptions } from './sqlDelimiterPolicy';
export {
  forgetMysqlSqlMode,
  mysqlAnsiQuotesFromSqlMode,
  mysqlMaskOptionsForConnection,
  mysqlMaskOptionsFromSqlMode,
  mysqlNoBackslashEscapesFromSqlMode,
} from './mysqlSqlMode';

/**
 * Replaces SQL string literals (`'…'`, including `''`-escape, plus `"…"`,
 * `E'…'`, `$tag$…$tag$` and `q'X…X'` on the engines where those are strings)
 * and `--` / `/* … *\/` comments inside `head` with spaces of equal length so
 * a literal like `'CREATE TABLE'` can't trigger a false-positive DDL match.
 * Delimited identifiers are copied through as opaque spans — DDL targets like
 * `CREATE TABLE [my table]` keep their identifier intact, and a `'`, `--`, or
 * `/*` *inside* the span (e.g. `"a'b"`, `` `x--y` ``) is a literal character,
 * not the start of a string or comment, so it can no longer "leak" and swallow
 * the identifier's closing delimiter.
 *
 * WHICH characters delimit what depends on the engine: `options` picks the row
 * (`sqlDelimiterPolicy.ts`), omitting `engine` falls back to its unknown row.
 *
 * A span ends at its closing delimiter, with a doubled closer (`''`, `""`,
 * `]]`, `` `` ``) read as one literal closer character rather than the end —
 * and, in a STRING on a grammar that escapes with backslashes (MySQL's
 * default, PostgreSQL's `E'…'`), a backslashed closer read the same way. An
 * unterminated span runs to the end of `head` (identifiers copied through
 * as-is, strings masked). See `sqlStringSpans.ts`.
 *
 * The output has exactly the same length/offsets as `head` — masked spans
 * become equal-length runs of spaces, and preserved spans (identifiers) are
 * copied through unchanged — since callers index into the original text.
 */
export function maskStringsAndComments(head: string, options?: MaskOptions): string {
  return maskSpans(head, options, 'mask-strings');
}

/**
 * Comments ONLY: `--` and block comments become equal-length space runs, while
 * string literals and delimited identifiers are copied through verbatim.
 *
 * For the one question that has to be asked of text with its strings intact: "is a
 * statement being EXECUTED here, and what does it say?". SQL Server's
 * `EXEC/*x*\/(N'CREATE/*y*\/DATABASE reporting')` is valid T-SQL twice over, and a
 * regex over the raw text matches neither half — see
 * `databaseDdlDetector.isDatabaseDdl`.
 * String spans are still WALKED rather than ignored, so a `--` or a `/*` inside a
 * literal cannot open a comment that swallows the rest of the statement.
 *
 * Same per-engine delimiter table, same equal-length / same-offsets guarantee.
 */
export function maskCommentsOnly(head: string, options?: MaskOptions): string {
  return maskSpans(head, options, 'keep-strings');
}

/**
 * Comments, reaching THROUGH string literals: same as {@link maskCommentsOnly}
 * outside strings, and inside each string span the comment SYNTAX is squashed to
 * spaces as well.
 *
 * For the second half of the dynamic-SQL question. The comment that hides a
 * dynamically executed statement can sit INSIDE the string carrying it —
 * `EXEC(N'CREATE/*x*\/DATABASE reporting')` and `EXEC(N'CREATE--x\nDATABASE r')`
 * are both valid T-SQL — and no string-respecting walker will touch it, because
 * as far as the OUTER SQL is concerned it is ordinary string content. Inside that
 * string it is a comment of the statement about to run, so it has to go.
 *
 * Why per-SPAN rather than one regex over the whole text (the shape this
 * replaced): a squash that ignores string boundaries altogether deletes
 * EXECUTABLE text. `SELECT '/*'; EXEC(N'CREATE DATABASE reporting'); SELECT '*\/';`
 * has an opener in the first literal and a closer in the last, so a boundary-blind
 * pass erases the whole middle — the EXEC included — and the classification MISSES
 * a real `CREATE DATABASE`. Bounding each squash to the span it started in makes
 * that impossible: nothing outside a string literal is ever removed, and one
 * literal's stray `/*` or `--` can never swallow another statement.
 *
 * Block comments are matched with the SAME per-engine nesting rule the walker's
 * out-of-string pass uses (`DelimiterPolicy.nestedBlockComments`): on T-SQL and
 * PostgreSQL `CREATE/*a/*b*\/c*\/DATABASE` is ONE comment, and a first-closer scan
 * would leave `c*\/` wedged between the keywords; on MySQL / Oracle / SQLite the
 * same text is a closed comment followed by `c*\/`, and a nesting scan would run
 * past the closer that really closes. Whether an engine nests is a documented fact
 * per grammar, not a guess — it lives in the `sqlDelimiterPolicy` table.
 *
 * Linear, and deliberately so — the caller runs after every successful query
 * execution. Same equal-length / same-offsets guarantee as its siblings; the OUTPUT
 * is a classification aid only, never text to re-parse (an unterminated `--` inside
 * a literal blanks that literal's closing quote along with the rest of the line).
 */
export function maskCommentsThroughStrings(head: string, options?: MaskOptions): string {
  return maskSpans(head, options, 'squash-comments-in-strings');
}

/**
 * What a STRING span becomes in the output:
 *  - `mask-strings` — an equal-length run of spaces ({@link maskStringsAndComments});
 *  - `keep-strings` — copied through verbatim ({@link maskCommentsOnly});
 *  - `squash-comments-in-strings` — copied through with its comment syntax
 *    blanked ({@link maskCommentsThroughStrings}).
 */
type SpanMaskMode = 'mask-strings' | 'keep-strings' | 'squash-comments-in-strings';

/**
 * Blanks `--`-to-end-of-line and `/* … *\/` inside ONE string span, preserving
 * length. `nested` is the engine's block-comment nesting rule
 * (`DelimiterPolicy.nestedBlockComments`), so an inner `/*` opens a nested comment
 * only where the grammar says it does.
 *
 * An opener that never closes gets its own two characters blanked and NOTHING
 * else: `/*` cannot be executable SQL in any dialect (there is no reading of it as
 * division), while the text after it can be. On a NESTING engine
 * `'CREATE/*a/*b*\/DATABASE r'` is an unterminated comment, so blanking the tail
 * would be a faithful reading — but the statement inside a literal is only a
 * candidate, and blanking is the one direction that can hide a real
 * `CREATE DATABASE` from the caller. Restoring is therefore the conservative
 * choice, and it costs one sweep.
 *
 * The caller (`databaseDdlDetector.isDatabaseDdl`) compares this output with the
 * un-squashed text and invalidates when they differ. Note what that comparison
 * really says: NOT "this literal carries comment syntax I could not resolve" but
 * "this literal carries comment syntax at all" — any `--` or `/*` inside any
 * literal changes the output. Conservative in the right direction, and cheap.
 *
 * ONE pass over the span, with a single restore sweep for the unclosed case — no
 * re-scan per opener. That is a requirement: the caller runs after every
 * successful execution, and a scan-per-opener over a literal holding one opener
 * every three characters is quadratic (see `databaseDdlDetector.perf.test.ts`).
 */
function squashCommentSyntax(span: string, nested: boolean): string {
  const out = span.split('');
  let i = 0;
  /** Block-comment nesting depth, and where the outermost still-open one began. */
  let depth = 0;
  let outermostOpener = -1;
  while (i < span.length) {
    // An inner `/*` opens a nested comment only where the engine's grammar says
    // so; elsewhere it is ordinary comment content and the first `*\/` closes.
    if (span[i] === '/' && span[i + 1] === '*' && (depth === 0 || nested)) {
      if (depth === 0) outermostOpener = i;
      depth++;
      out[i] = ' ';
      out[i + 1] = ' ';
      i += 2;
      continue;
    }
    if (depth > 0) {
      if (span[i] === '*' && span[i + 1] === '/') {
        depth--;
        out[i] = ' ';
        out[i + 1] = ' ';
        i += 2;
        if (depth === 0) outermostOpener = -1;
        continue;
      }
      out[i] = ' ';
      i++;
      continue;
    }
    if (span[i] === '-' && span[i + 1] === '-') {
      while (i < span.length && span[i] !== '\n') {
        out[i] = ' ';
        i++;
      }
      continue;
    }
    i++;
  }
  if (outermostOpener >= 0) {
    for (let k = outermostOpener + 2; k < span.length; k++) out[k] = span[k];
  }
  return out.join('');
}

function maskSpans(head: string, options: MaskOptions | undefined, mode: SpanMaskMode): string {
  const policy = policyFor(options);
  /** A STRING span — see {@link SpanMaskMode}. */
  const emitString = (from: number, to: number): string =>
    mode === 'mask-strings'
      ? ' '.repeat(to - from)
      : mode === 'keep-strings'
        ? head.slice(from, to)
        : squashCommentSyntax(head.slice(from, to), policy.nestedBlockComments);
  let out = '';
  let i = 0;
  while (i < head.length) {
    const ch = head[i];
    if (
      (ch === '"' && policy.doubleQuote === 'identifier') ||
      (ch === '`' && policy.backtickIdentifier) ||
      (ch === '[' && policy.bracketIdentifier)
    ) {
      const end = delimitedSpanEnd(head, i, ch === '[' ? ']' : ch);
      out += head.slice(i, end);
      i = end;
      continue;
    }
    // PostgreSQL `E'…'`: a string whose backslashes escape — the `E` belongs
    // to the literal, so the span starts one character before its quote.
    if (policy.escapeStringPrefix && isEscapeStringLead(head, i)) {
      const end = delimitedSpanEnd(head, i + 1, "'", true);
      out += emitString(i, end);
      i = end;
      continue;
    }
    if (ch === "'" || (ch === '"' && policy.doubleQuote === 'string')) {
      const end = delimitedSpanEnd(head, i, ch, policy.backslashEscapesStrings);
      out += emitString(i, end);
      i = end;
      continue;
    }
    // `$…$` (PG) and `q'…'` (Oracle) only open a literal where a token can
    // start: in `a$$b$$` and `myq'v'` the `$` / `q` CONTINUES the identifier
    // before it, so what follows is an ordinary string at most.
    if (policy.dollarQuotedString && ch === '$' && !isIdentifierChar(head[i - 1])) {
      const end = dollarQuotedSpanEnd(head, i);
      if (end !== null) {
        out += emitString(i, end);
        i = end;
        continue;
      }
    }
    if (policy.qQuotedString && Q_QUOTE_LEAD.test(ch) && !isIdentifierChar(head[i - 1])) {
      const end = qQuotedSpanEnd(head, i);
      if (end !== null) {
        out += emitString(i, end);
        i = end;
        continue;
      }
    }
    if (ch === '-' && head[i + 1] === '-') {
      while (i < head.length && head[i] !== '\n') {
        out += ' ';
        i++;
      }
      continue;
    }
    if (ch === '/' && head[i + 1] === '*') {
      // WHERE a block comment ends is a per-engine grammar fact
      // (`policy.nestedBlockComments`): T-SQL and PostgreSQL count an inner `/*`
      // and end at the MATCHING closer, the others end at the FIRST one. Reading
      // it wrong in either direction is a false negative — on a nesting engine a
      // first-closer scan leaves `c*\/` wedged between `CREATE` and `DATABASE`,
      // and on a non-nesting engine a nesting scan swallows the statement AFTER
      // the comment. An unterminated comment runs to the end of `head` either way,
      // which is what every engine reads there.
      out += '  ';
      i += 2;
      let depth = 1;
      while (i < head.length) {
        if (policy.nestedBlockComments && head[i] === '/' && head[i + 1] === '*') {
          depth++;
          out += '  ';
          i += 2;
          continue;
        }
        if (head[i] === '*' && head[i + 1] === '/') {
          depth--;
          out += '  ';
          i += 2;
          if (depth === 0) break;
          continue;
        }
        out += ' ';
        i++;
      }
      continue;
    }
    out += ch;
    i++;
  }
  return out;
}
